/**
 * screens/jobs/jobs.tsx
 * Jobs (requisitions) list — wired to GET /api/hire/v2/requisitions.
 * Premium row-cards (staggered entrance + hover lift) with per-job Edit and
 * Close (archive) actions:
 *   - Edit  → fetches the current revision, opens the pre-filled form; saving
 *             asks for confirmation and shows a success dialog (append-revision).
 *   - Close → confirmation dialog → POST /:id/close (status→closed, append-only,
 *             never a hard delete) → success dialog.
 * Every write goes through the service layer; all outcomes surface via the
 * centralized toast / dialog components.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useWorkspaceStats } from '../../hooks/useAiUsage';
import { ActionButton, StatusChip, Icon, CommonSearch, StatChip, Pagination, EmptyState } from '../../components/ui';
import ConfirmDialog from '../../components/confirmDialog';
import SuccessDialog from '../../components/successDialog';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
// "+ New job" / Edit share the same create/edit form (structured fields + paste-a-JD extract).
import NewJobModal from '../../components/hire/newJobModal';
// "Bulk import" → many JD files in one pass (extract + create per file).
import BulkUploadModal from '../../components/hire/bulkUploadModal';
import { listRequisitions, getRequisition, closeRequisition } from '../../services/hire/requisitionV2';
import type { RequisitionListItem, RequisitionListResult, RequisitionDetail, ReqStatus } from '../../schemas/hire/requisitionV2';
import type { AppStackParamList } from '../../navigation/types';
import { reqStatusVM, expBand } from './jobsVm';

type Nav = NativeStackNavigationProp<AppStackParamList>;

// Filter checkboxes. Each is an INDEPENDENT toggle (pressing one never touches
// the others). "All" is its own key — when checked it shows every job; when
// unchecked the list is filtered to whichever individual statuses are ticked.
type FilterKey = 'all' | ReqStatus;
const STATUS_FILTERS: { key: ReqStatus; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'draft', label: 'Draft' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'closed', label: 'Closed' },
];
const STATUS_KEYS: ReqStatus[] = STATUS_FILTERS.map((f) => f.key);
// "All" is a shortcut for the OPEN statuses only — it never includes Closed.
// Closed shows solely when its own tab is selected.
const OPEN_KEYS: ReqStatus[] = ['active', 'draft', 'on_hold'];
// Search scope: the statuses a recruiter can still act on. On-hold and closed
// requisitions are excluded so a search lands on workable jobs only.
const SEARCH_KEYS: ReqStatus[] = ['active', 'draft'];
// Single-select tabs: exactly one filter is active at a time. Clicking a tab
// isolates to it (empty statuses → the premium empty screen). Default = All.
const DEFAULT_SELECTION: FilterKey[] = ['all'];

/** Premium staggered entrance (web-only; inert on native). */
function rise(delay: number) {
  return web({ animation: 'sk-rise 460ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` });
}

/**
 * Premium filter checkbox with a live count. Independent, self-contained toggle:
 * pressing it flips only its own `checked` state (never cascades to siblings).
 * Highlights (accent border + soft fill + filled tick) when checked.
 */
function FilterCheck({
  label, count, checked, onToggle,
}: { label: string; count: number; checked: boolean; onToggle: () => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...hoverProps}
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      style={[
        {
          flexDirection: 'row', alignItems: 'center', gap: 9, height: 34, paddingHorizontal: 12,
          borderRadius: radii.md, borderWidth: 1,
          borderColor: checked ? colors.accent : hovered ? colors.lineStrong : colors.line,
          backgroundColor: checked ? colors.accentSoft : hovered ? colors.paper2 : colors.card,
        },
        web({ cursor: 'pointer', transition: 'background 150ms ease, border-color 150ms ease, transform 150ms ease' }),
        hovered && web({ transform: 'translateY(-1px)' }),
      ]}
    >
      <View
        style={[
          {
            width: 17, height: 17, borderRadius: 5, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center',
            borderColor: checked ? colors.accent : colors.lineStrong,
            backgroundColor: checked ? colors.accent : 'transparent',
          },
          web({ transition: 'background 150ms ease, border-color 150ms ease' }),
        ]}
      >
        {checked ? <Icon name="check" size={11} color={colors.onAccent} /> : null}
      </View>
      <Text style={{ fontSize: fontSize.sm, fontWeight: checked ? fontWeight.semibold : fontWeight.medium, color: checked ? colors.accentInk : colors.ink2 }}>{label}</Text>
      <View style={{ minWidth: 20, height: 18, paddingHorizontal: 6, alignItems: 'center', justifyContent: 'center', borderRadius: 9, backgroundColor: checked ? colors.accent : colors.paper3 }}>
        <Text style={{ fontSize: 11, fontWeight: fontWeight.semibold, color: checked ? colors.onAccent : colors.ink3 }}>{count}</Text>
      </View>
    </Pressable>
  );
}


// Distinct accent palettes for the row actions (the only splash of hue in the
// otherwise-grayscale system). Edit = blue, Close/delete = red.
const ICON_TONES = {
  edit:   { base: '#6B86D6', active: '#3B6FE0', soft: 'rgba(59,111,224,0.14)',  ring: 'rgba(59,111,224,0.55)',  glow: 'rgba(59,111,224,0.45)' },
  delete: { base: '#D08585', active: '#E04848', soft: 'rgba(224,72,72,0.14)',   ring: 'rgba(224,72,72,0.55)',   glow: 'rgba(224,72,72,0.45)' },
} as const;

/** Premium square action icon (Edit / Close): soft tinted fill, colored ring,
 *  lift + subtle scale + a colored glow on hover. Each kind has its own hue. */
function IconBtn({
  name, label, kind, loading, onPress,
}: { name: string; label: string; kind: keyof typeof ICON_TONES; loading?: boolean; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { hovered, hoverProps } = useHover();
  const t = ICON_TONES[kind];
  const activeTint = t.active;
  const softBg = t.soft;
  const ring = t.ring;
  const glow = t.glow;
  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      accessibilityLabel={label}
      disabled={loading}
      style={[
        {
          width: 36, height: 36, alignItems: 'center', justifyContent: 'center',
          borderRadius: 11, borderWidth: 1,
          borderColor: hovered ? ring : colors.line,
          backgroundColor: hovered ? softBg : colors.paper2,
        },
        web({ cursor: 'pointer', transition: 'transform 170ms cubic-bezier(.2,.7,.2,1), background 170ms ease, border-color 170ms ease, box-shadow 170ms ease' }),
        hovered && web({ transform: 'translateY(-2px) scale(1.05)', boxShadow: `0 8px 18px -9px ${glow}66` }),
      ]}
    >
      {loading ? <ActivityIndicator size="small" color={activeTint} /> : <Icon name={name} size={16} color={hovered ? activeTint : colors.ink3} />}
    </Pressable>
  );
}

function Row({
  item, index, pageOffset = 0, onOpen, onEdit, onCloseJob, editLoading,
}: {
  item: RequisitionListItem; index: number; pageOffset?: number;
  onOpen: () => void; onEdit: () => void; onCloseJob: () => void; editLoading: boolean;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const vm = reqStatusVM(item.status);
  const client = item.inHouse ? 'In-house' : 'Client account';
  const must = item.mustHaveSkills.slice(0, 3).join(', ') || '—';
  const closed = item.status === 'closed';
  return (
    <View
      {...hoverProps}
      style={[
        { backgroundColor: colors.card, borderColor: hovered ? colors.lineStrong : colors.line, borderWidth: 1, borderRadius: radii.lg, padding: 16, opacity: closed ? 0.72 : 1 },
        rise(60 + index * 55),
        web({ transition: 'border-color 140ms ease, transform 140ms ease, box-shadow 140ms ease' }),
        hovered && web({ transform: 'translateY(-2px)', boxShadow: '0 8px 24px -14px rgba(0,0,0,0.28)' }),
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
        {/* Serial in a subtle badge — keeps the row index aligned with the title
            baseline and reads as a quiet ordinal rather than floating text. */}
        <View style={{ minWidth: 26, height: 26, paddingHorizontal: 6, borderRadius: 8, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
          <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold }}>{pageOffset + index + 1}</Text>
        </View>
        <Pressable onPress={onOpen} style={[{ flex: 1, gap: 8, minWidth: 0 }, web({ cursor: 'pointer' })]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.2 }}>{item.title}</Text>
            <StatusChip tone={vm.tone} label={vm.label} />
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4 }}>{item.code} · rev {item.currentRevision}</Text>
          </View>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, lineHeight: 20 }}>
            {client} · {item.openingsCount} opening{item.openingsCount === 1 ? '' : 's'} · must-have: {must} · {expBand(item.experienceMin, item.experienceMax)}
          </Text>
        </Pressable>

        <View style={[{ flexDirection: 'row', gap: 6 }, web({ opacity: hovered ? 1 : 0.72, transition: 'opacity 140ms ease' })]}>
          <IconBtn name="edit" label="Edit job" kind="edit" loading={editLoading} onPress={onEdit} />
          {!closed && <IconBtn name="archive" label="Close job" kind="delete" onPress={onCloseJob} />}
        </View>
      </View>
    </View>
  );
}

export default function JobsScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { isDark } = useTheme();
  const { isMobile } = useBreakpoints();
  const navigation = useNavigation<Nav>();

  const [result, setResult] = useState<RequisitionListResult | null>(null);
  // Independent multi-select filter. "All" (when ticked) shows every job;
  // otherwise the list is the union of the ticked statuses. Default = All +
  // active + draft + on hold ticked, Closed unticked. Search and per-status
  // counts come from the server; the status filter + pagination run client-side.
  const [selected, setSelected] = useState<Set<FilterKey>>(() => new Set(DEFAULT_SELECTION));
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<RequisitionDetail | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [pendingClose, setPendingClose] = useState<RequisitionListItem | null>(null);
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState<RequisitionDetail | null>(null);

  const PAGE_SIZE = 10;

  // Fetch the full q-matched set once (status='all' returns every status plus
  // the aggregate per-status counts); the multi-select status filter and
  // pagination are then derived client-side. The org set is small and already
  // materialized server-side, so one request per search term is cheap. A
  // per-request id + abort drops stale responses while typing.
  const reqIdRef = useRef(0);
  const fetchList = useCallback((q: string) => {
    const myReq = ++reqIdRef.current;
    const controller = new AbortController();
    listRequisitions({ q, status: 'all', page: 1, pageSize: 100 }, controller.signal)
      .then((r) => { if (myReq === reqIdRef.current) setResult(r); })
      .catch(() => {
        if (myReq === reqIdRef.current && !controller.signal.aborted) {
          setResult({ items: [], page: 1, pageSize: 100, total: 0, counts: { all: 0, active: 0, draft: 0, on_hold: 0, closed: 0 } });
        }
      });
    return () => controller.abort();
  }, []);

  // One callback drives initial load, live search and focus. Status/page changes
  // are pure client-side derivations (no refetch).
  const refresh = useCallback(() => fetchList(debouncedQuery), [fetchList, debouncedQuery]);
  useFocusEffect(refresh);

  // Live: refetch when something changes elsewhere (SSE rev bump), DEBOUNCED so a
  // burst of changes (or our own mutations) collapses into one refetch.
  //
  // Two guards keep this from stampeding the API:
  //  1. isFocused — native-stack keeps prior screens MOUNTED underneath the top
  //     one, so an unguarded rev effect makes every mounted list refetch on the
  //     same bump. We only refetch the visible screen; useFocusEffect(refresh)
  //     above catches a backgrounded screen up the moment it's shown again.
  //  2. refresh via ref — reading refresh from a ref (instead of an effect dep)
  //     means a query/status/page change no longer re-runs this effect and
  //     clears the pending debounce timer, so bursts actually coalesce.
  const isFocused = useIsFocused();
  const { rev } = useWorkspaceStats();
  const lastRev = useRef(rev);
  const revTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    // While backgrounded, absorb bumps so useFocusEffect(refresh) owns the
    // single catch-up fetch on refocus (no duplicate refetch here).
    if (!isFocused) { lastRev.current = rev; return; }
    if (rev === lastRev.current) return;
    lastRev.current = rev;
    if (revTimer.current) clearTimeout(revTimer.current);
    // 1.2s, not 300ms: one user action bumps `rev` several times server-side
    // (record + consent + derived profile + AI spend), and at 300ms each bump
    // landed as its own list fetch.
    revTimer.current = setTimeout(() => refreshRef.current(), 1_200);
    return () => { if (revTimer.current) clearTimeout(revTimer.current); };
  }, [rev, isFocused]);

  const openDetail = (id: string) => navigation.navigate('jobDetail', { id });
  // Navigates straight to the new job; the list refetches on focus return.
  const onCreated = (r: RequisitionDetail) => { setModalOpen(false); openDetail(r.id); };

  const onSearch = (t: string) => { setQuery(t); setPage(1); };
  // Single-select tabs — clicking a tab isolates to it (empty statuses then
  // show the premium empty screen). Re-clicking the active tab is a no-op.
  const toggleKey = (key: FilterKey) => { setSelected(new Set([key])); setPage(1); };
  // Empty-state shortcut: jump back to the "All" tab.
  const showAll = () => { setSelected(new Set(['all'])); setPage(1); };

  // Edit → fetch the current revision, then open the pre-filled form.
  const onEditClick = async (item: RequisitionListItem) => {
    setEditLoadingId(item.id);
    try {
      const detail = await getRequisition(item.id);
      setEditing(detail);
    } catch (e) {
      showToast('error', 'Could not open the job', getApiErrorMessage(e));
    } finally {
      setEditLoadingId(null);
    }
  };

  // Close → confirmed archive.
  const doClose = async () => {
    if (!pendingClose) return;
    const target = pendingClose;
    setClosing(true);
    // Optimistic: reflect the closed status immediately; the debounced SSE
    // reconcile then drops it out of the active page.
    setResult((prev) => (prev ? { ...prev, items: prev.items.map((it) => (it.id === target.id ? { ...it, status: 'closed' } : it)) } : prev));
    try {
      const detail = await closeRequisition(target.id);
      setPendingClose(null);
      setClosed(detail); // success dialog
    } catch (e) {
      showToast('error', 'Could not close the job', getApiErrorMessage(e));
      refresh(); // failed → restore true state
    } finally {
      setClosing(false);
    }
  };

  const loading = result === null;
  const counts = result?.counts ?? { all: 0, active: 0, draft: 0, on_hold: 0, closed: 0 };
  const searching = debouncedQuery.trim().length > 0;
  // Live match count shown in the search field — scoped to the same statuses
  // the search results are restricted to.
  const searchScopeCount = SEARCH_KEYS.reduce((sum, k) => sum + counts[k], 0);
  const hasAnyJobs = counts.all > 0;

  // Filter derivations. Visible statuses = the union of the ticked ones, where
  // "All" contributes the OPEN statuses (active/draft/on_hold) but NOT closed.
  // Closed is included only when its own box is ticked. Nothing ticked → empty.
  //
  // While SEARCHING we restrict results to the OPEN-FOR-WORK statuses — active
  // and draft (the status tabs are bypassed). Search is for finding a
  // requisition you can still act on; closed and on-hold ones stay out.
  const allChecked = selected.has('all');
  const activeStatuses = searching
    ? STATUS_KEYS.filter((k) => SEARCH_KEYS.includes(k))
    : STATUS_KEYS.filter((k) => selected.has(k) || (allChecked && k !== 'closed'));
  const openCount = OPEN_KEYS.reduce((sum, k) => sum + counts[k], 0);
  const noneSelected = activeStatuses.length === 0;
  // Status filter applied client-side over the full q-matched set.
  const filtered = (result?.items ?? []).filter((it) => activeStatuses.includes(it.status));
  const filteredTotal = filtered.length;
  const pageCount = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const items = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  // Human-readable list of the active statuses (for empty copy).
  const selectedLabels = STATUS_FILTERS.filter((f) => activeStatuses.includes(f.key)).map((f) => f.label).join(' · ');

  // Status filter chips (All + each status). Shared by the desktop wrapping row
  // and the mobile horizontal-scroll strip so both stay in sync.
  const filterControls = (
    <>
      <FilterCheck label="All" count={openCount} checked={allChecked} onToggle={() => toggleKey('all')} />
      <View style={{ width: 1, height: 20, backgroundColor: colors.line, marginHorizontal: 2 }} />
      {STATUS_FILTERS.map((f) => (
        <FilterCheck
          key={f.key}
          label={f.label}
          count={counts[f.key]}
          checked={selected.has(f.key)}
          onToggle={() => toggleKey(f.key)}
        />
      ))}
    </>
  );

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 60 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 16 }}>
        <View style={[styles.header, { alignItems: 'center' }, rise(20)]}>
          <Text style={{ fontSize: 22, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.4 }}>Jobs</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <ActionButton label="Bulk import" variant="secondary" icon="upload" onPress={() => setBulkOpen(true)} />
            <ActionButton label="New job" variant="primary" icon="plus" onPress={() => setModalOpen(true)} />
          </View>
        </View>

        {/* Premium totals — the workspace snapshot at a glance. Hidden while
            searching (the search field shows its own live match count). */}
        {!loading && !searching && hasAnyJobs && (
          <View style={[{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }, rise(26)]}>
            {/* Mobile: a clean stat hierarchy — the emphasised total spans the
                row, the two sub-stats split the next row as equal halves. */}
            <View style={isMobile ? { flexBasis: '100%' } : undefined}>
              <StatChip value={openCount} label="Total jobs" emphasis selected={allChecked} onPress={() => toggleKey('all')} />
            </View>
            <View style={isMobile ? { flexGrow: 1, flexBasis: 0, minWidth: 0 } : undefined}>
              <StatChip value={counts.active} label="Active" tone="success" selected={selected.has('active')} onPress={() => toggleKey('active')} />
            </View>
            <View style={isMobile ? { flexGrow: 1, flexBasis: 0, minWidth: 0 } : undefined}>
              <StatChip value={counts.closed} label="Closed" tone="neutral" selected={selected.has('closed')} onPress={() => toggleKey('closed')} />
            </View>
          </View>
        )}

        {/* Search — server-ranked by title, code and must-have skills. Stays
            visible during a zero-result search so the term can be cleared. */}
        {!loading && (hasAnyJobs || searching) && (
          <View style={[{ width: '100%', maxWidth: isMobile ? undefined : 440 }, rise(32)]}>
            <CommonSearch
              value={query}
              onChange={onSearch}
              isDark={isDark}
              placeholder="Search active & draft jobs by title, code or skill…"
              count={searching ? searchScopeCount : null}
              countNoun="match"
            />
          </View>
        )}

        {/* While searching we bypass the status tabs (results are active+draft),
            so show just pagination on its own right-aligned row. */}
        {!loading && searching && pageCount > 1 && (
          <View style={[{ flexDirection: 'row', justifyContent: 'flex-end' }, rise(40)]}>
            <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
          </View>
        )}

        {/* Independent filter checkboxes (left) + pagination (right) on one row.
            Each toggles only itself. "All" ticked shows every job; default is
            All + Active + Draft + On hold ticked, Closed unticked. Hidden while
            searching (search is active+draft only). */}
        {!loading && hasAnyJobs && !searching && (
          isMobile ? (
            // Mobile: tabs get their own full-width horizontal scroll strip (so
            // All/Active/Draft/Closed are all reachable) with a visible
            // scrollbar; pagination sits on its own row below, right-aligned.
            <View style={[{ gap: 14 }, rise(40)]}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator
                {...({ dataSet: { skScroll: 'x' } } as object)}
                contentContainerStyle={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingBottom: 10, paddingRight: 4 }}
              >
                {filterControls}
              </ScrollView>
              <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
              </View>
            </View>
          ) : (
            <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }, rise(40)]}>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {filterControls}
              </View>
              <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
            </View>
          )
        )}

        {loading ? (
          <View style={{ paddingVertical: 60 }}><ActivityIndicator size="large" color={colors.accent} /></View>
        ) : !hasAnyJobs && !searching ? (
          <EmptyState
            icon="layers"
            title="No jobs yet"
            subtitle="Create your first requisition to start matching candidates against your pipeline."
            action={{ label: 'New job', icon: 'plus', variant: 'primary', onPress: () => setModalOpen(true) }}
          />
        ) : searching && filteredTotal === 0 ? (
          <EmptyState
            icon="search"
            title={`No active or draft jobs match “${debouncedQuery.trim()}”`}
            subtitle="Search only shows active and draft jobs. Try a different title, code or skill — or clear the search."
            action={{ label: 'Clear search', icon: 'x', variant: 'secondary', onPress: () => onSearch('') }}
          />
        ) : noneSelected ? (
          <EmptyState
            icon="sliders"
            title="No filters selected"
            subtitle="Pick at least one status above to see jobs, or show them all at once."
            action={{ label: 'Show all jobs', icon: 'check', variant: 'primary', onPress: showAll }}
          />
        ) : filteredTotal === 0 ? (
          <EmptyState
            icon="layers"
            title={`No ${(selectedLabels || 'matching').toLowerCase()} jobs`}
            subtitle={`There are no ${(selectedLabels || 'matching').toLowerCase()} jobs right now. Switch to another tab or view them all.`}
            action={{ label: 'Show all jobs', icon: 'layers', variant: 'primary', onPress: showAll }}
          />
        ) : (
          <View style={{ gap: 12 }}>
            {items.map((it, i) => (
              <Row
                key={it.id}
                item={it}
                index={i}
                pageOffset={(safePage - 1) * PAGE_SIZE}
                editLoading={editLoadingId === it.id}
                onOpen={() => openDetail(it.id)}
                onEdit={() => onEditClick(it)}
                onCloseJob={() => setPendingClose(it)}
              />
            ))}
          </View>
        )}
      </View>

      {/* Create + Edit share one form. Edit carries its own confirm + success dialogs. */}
      <NewJobModal
        open={modalOpen || !!editing}
        editing={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onCreated={onCreated}
        onUpdated={() => { setEditing(null); refresh(); }}
      />

      {/* Bulk import — many JD files at once. Refresh the list on completion so
          the newly created jobs are visible without a manual reload. */}
      <BulkUploadModal
        open={bulkOpen}
        kind="job"
        onClose={() => setBulkOpen(false)}
        onDone={() => refresh()}
      />

      {/* Close (archive) → confirm before the write. */}
      <ConfirmDialog
        visible={!!pendingClose}
        title="Close this job?"
        message={`"${pendingClose?.title ?? ''}" will stop matching and move to Closed. Nothing is deleted — every revision stays in history and you can reopen it later.`}
        confirmText="Close job"
        cancelText="Keep open"
        busy={closing}
        onConfirm={doClose}
        onCancel={() => setPendingClose(null)}
      />

      {/* Close → success moment. */}
      <SuccessDialog
        visible={!!closed}
        title="Job closed"
        message={`"${closed?.title ?? ''}" is now closed. It's archived, not deleted.`}
        primaryAction={{ label: 'Done', onPress: () => setClosed(null) }}
        onClose={() => setClosed(null)}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
});
